/**
 * 試験問題ページ
 *************************************************/
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  Container,
  Divider,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  Segment,
  Image,
  DropdownProps,
  ButtonProps,
  InputOnChangeData,
} from 'semantic-ui-react';
import { API, Auth, graphqlOperation } from 'aws-amplify';
import { createAnswer } from 'graphql/mutations';
import { v4 as uuidv4 } from 'uuid';
import Footer from 'components/common/Footer';
import TimeUpModal from 'components/common/TimeUpModal';
import ConfirmDialog from 'components/common/ConfirmDialog';
import { startLoading, endLoading } from 'modules/commonModule';
import ExampleModal from './ExampleModal';

type LocationState = {
  userId: string;
  categoryNo: number;
  timeLimit: number;
  quizImagePath: string;
  quizzes: any;
  example: {
    description: string;
    imagePath: string | null;
  };
};
const QuestionPage: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<LocationState>();
  const { userId, categoryNo, quizImagePath, quizzes, example } = location.state;
  const [time, setTime] = useState<number>(Number(sessionStorage.getItem('time')));
  const [intervalID, setIntervalID] = useState<number>();
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [isOpenExampleModal, setIsOpenExampleModal] = useState<boolean>(false);
  const [isOpenTimeUpModal, setIsOpenTimeUpModal] = useState<boolean>(false);
  const [condition, setCondition] = useState<any>({});
  const areaRef = useRef<HTMLDivElement>(null);

  // スクロール位置をTOPに
  useEffect(() => {
    if (areaRef.current) {
      console.log(areaRef.current);
      console.log(areaRef.current.offsetHeight);
      window.scrollTo(0, areaRef.current.offsetTop);
    }
  }, []);

  // 解答選択時の処理
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
      const inputAnswer = {
        id: uuidv4(),
        userId,
        quizId: data.id,
        answer: data.value.trim(), // trim関数で文字列の空白を除去
      };

      setCondition({ ...condition, [data.name]: inputAnswer });
    },
    [condition, userId],
  );

  const handleSelectChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: ButtonProps) => {
      const inputAnswer = {
        id: uuidv4(),
        userId,
        quizId: data.id,
        answer: data.value,
      };

      setCondition({ ...condition, [data.name]: inputAnswer });
    },
    [condition, userId],
  );

  const handleSelectChange2 = useCallback(
    (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
      const { name, value, options } = data;
      const targetOption = options?.find((option) => option.value === value);

      if (targetOption) {
        const side = targetOption['data-side'];
        const quizId = targetOption.id;

        const inputAnswer = {
          id: uuidv4(),
          userId,
          quizId,
          leftAnswer: side === 'left' ? value : condition[name]?.leftAnswer || '',
          rightAnswer: side === 'right' ? value : condition[name]?.rightAnswer || '',
        };
        setCondition({ ...condition, [name]: inputAnswer });
      }
    },
    [condition, userId],
  );

  /**
   * サブミット処理
   */
  const onSubmit = useCallback(async () => {
    try {
      dispatch(startLoading());

      // ダイアログ or モーダルを閉じる
      setIsOpenDialog(false);
      setIsOpenTimeUpModal(false);

      // 解答内容を登録
      Object.keys(condition).forEach((key) => {
        delete condition[key].leftAnswer;
        delete condition[key].rightAnswer;

        API.graphql(graphqlOperation(createAnswer, { input: condition[key] }));
      });

      const user = await Auth.currentAuthenticatedUser();

      // ユーザーステータスと表示中の問題番号が一致しなければ
      // 解答済の問題と判定してログイン画面へリダイレクト
      if (user.attributes['custom:status'] !== String(categoryNo) && user.attributes['custom:role'] !== 'tester') {
        history.push('/');
      }

      if (categoryNo < 5) {
        // 例題の制限時間をセッションストレージに保持（次画面の初期値として利用）
        sessionStorage.setItem('time', '300000');

        if (user.attributes['custom:status'] && user.attributes['custom:role'] !== 'tester') {
          // ユーザーステータスを更新
          await Auth.updateUserAttributes(user, {
            'custom:status': String(categoryNo + 1),
          });
        }

        // 試験例題へ遷移
        history.push('/example', { userId, categoryNo: categoryNo + 1 });
      } else {
        if (user.attributes['custom:status'] && user.attributes['custom:role'] !== 'tester') {
          // ユーザーステータスを更新
          const now = moment();
          await Auth.updateUserAttributes(user, {
            'custom:status': String(categoryNo + 1),
            'custom:endAt': now.format('YYYY-MM-DD HH:mm'),
          });
        }

        // 試験完了へ遷移
        history.push('/complete', { categoryNo: categoryNo + 1 });
      }
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(endLoading());
    }
  }, [categoryNo, condition, dispatch, history, userId]);

  // カウントダウン処理
  const countdown = useCallback(() => {
    setTime((prevTime: number) => {
      const remainingTime = prevTime - 1000;

      // セッションストレージに現時間を保持（画面リロード対策）
      sessionStorage.setItem('time', String(remainingTime));

      return remainingTime;
    });
  }, []);

  // 1秒ごとにカウントダウン処理を実行
  useEffect(() => {
    const id = window.setInterval(countdown, 1000);
    setIntervalID(id);
  }, [countdown]);

  // 制限時間切れ時に発火する処理
  useEffect(() => {
    if (time <= 0) {
      clearInterval(intervalID);
      setIsOpenTimeUpModal(true);
    }
  }, [countdown, intervalID, time]);

  // ブラウザバックを禁止
  useEffect(() => {
    history.push('/question', location.state);
  }, [history, location.state]);

  useEffect(() => {
    window.onpopstate = () => {
      return () => {
        window.onpopstate = null;
      };
    };
  }, []);

  //  ブラウザクローズ時の確認メッセージ;
  useEffect(() => {
    window.onbeforeunload = (e: BeforeUnloadEvent) => {
      e.preventDefault();

      return '';
    };
  }, []);

  useEffect(() => {
    (async () => {
      try {
        dispatch(startLoading());

        // 認証チェック
        const user = await Auth.currentAuthenticatedUser();

        // ユーザーステータスと表示中の問題番号が一致しなければ
        // 解答済の問題と判定してログイン画面へリダイレクト
        if (user.attributes['custom:status'] !== String(categoryNo) && user.attributes['custom:role'] !== 'tester') {
          history.push('/');
        }
      } catch (e) {
        console.log(e);
        // 認証できなかった場合はログイン画面へリダイレクト
        if (e === 'not authenticated') {
          history.push('/');
        }
      } finally {
        dispatch(endLoading());
      }
    })();
  }, [categoryNo, dispatch, history]);

  return (
    <>
      <div ref={areaRef} />
      <Container
        textAlign="center"
        fluid
        style={{
          background: 'black',
          position: 'sticky',
          top: 0,
          zIndex: 999,
        }}
      >
        <Header as="h1" size="large" style={{ padding: '15px 0', color: 'white' }}>
          {`残り時間：${moment(time).format('mm分ss秒')}`}
        </Header>
        <Button
          color="google plus"
          size="large"
          onClick={() => setIsOpenExampleModal(true)}
          style={{
            position: 'absolute',
            left: '15px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 999,
          }}
        >
          問題のやり方
          <Icon name="external alternate" style={{ marginLeft: '10px' }} />
        </Button>
      </Container>
      <Container
        textAlign="center"
        fluid
        style={{
          background: 'white',
          position: 'sticky',
          top: '60px',
          zIndex: 999,
          margin: 0,
          padding: '5px 0',
        }}
      >
        <Divider section horizontal>
          <Header as="h1" size="huge">
            {`問題${categoryNo}`}
          </Header>
        </Divider>
      </Container>

      {quizImagePath && (
        <Container
          textAlign="center"
          fluid
          style={{
            background: 'white',
            position: 'sticky',
            top: '158px',
            zIndex: 999,
            margin: 0,
          }}
        >
          <Grid.Row>
            <Grid.Column width={16}>
              <Image src={`${process.env.PUBLIC_URL}/image${quizImagePath}`} centered size="large" />
            </Grid.Column>
          </Grid.Row>
        </Container>
      )}

      <Container style={{ marginTop: '5px', marginBottom: '70px' }}>
        <Form size="large" autocomplete="off">
          <Grid container textAlign="left" columns={2} style={{ marginBottom: '50px' }}>
            {quizzes?.map((quiz: any) => (
              <>
                <Grid.Column key={quiz.id}>
                  <Grid columns={2}>
                    <Grid.Column width={10}>
                      <p style={{ fontSize: '1.3rem', margin: '0' }}>
                        <span style={{ marginRight: '20px' }}>{`問${`00${quiz.no}`.slice(-2)}.`}</span>
                        <span style={{ fontFamily: 'MS Pゴシック, sans-serif', letterSpacing: '3px' }}>
                          {quiz.question}
                        </span>
                      </p>
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Form.Input
                        name={`quizNo${quiz.no}`}
                        id={quiz.id}
                        size="mini"
                        onChange={handleChange}
                        maxLength="6"
                      />
                    </Grid.Column>
                  </Grid>
                </Grid.Column>
              </>
            ))}
          </Grid>

          <Divider />

          <Grid container>
            <Grid.Row centered>
              <Grid.Column width={6}>
                <Button fluid primary size="huge" onClick={() => setIsOpenDialog(true)}>
                  {categoryNo === 5 ? '試験を終了する' : '次の問題へ進む'}
                  <Icon name="arrow right" />
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>

          <ExampleModal isOpen={isOpenExampleModal} setIsOpen={setIsOpenExampleModal} example={example} />
          <ConfirmDialog
            isOpen={isOpenDialog}
            setIsOpen={setIsOpenDialog}
            action={onSubmit}
            currentPage="question"
            categoryNo={categoryNo}
          />
          <TimeUpModal isOpen={isOpenTimeUpModal} action={onSubmit} currentPage="example" categoryNo={categoryNo} />
        </Form>
      </Container>
      <Footer />
    </>
  );
};
export default QuestionPage;
